// File: /opt/dev_environment/assets/workspace/app-init/src/components/DataExportSection/DataExportSection.js

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useVocabulary } from '../../context/VocabularyContext';
import { downloadCsvFiles, sendCsvViaEmail } from '../../services/exportService';
import './DataExportSection.css';

function DataExportSection() {
  const { cards, verbCards, learnedWords, learnedVerbs, reviews } = useVocabulary();

  const [downloadState, setDownloadState] = useState('idle');
  const [emailState, setEmailState] = useState('idle');
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailError, setEmailError] = useState('');
  const [toastMessage, setToastMessage] = useState(null);

  const toastTimerRef = useRef(null);
  const emailInputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (showEmailDialog && emailInputRef.current) {
      setTimeout(() => emailInputRef.current?.focus(), 100);
    }
  }, [showEmailDialog]);

  const showToast = useCallback((type, text) => {
    setToastMessage({ type, text });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setToastMessage(null);
    }, 4000);
  }, []);

  const handleDownload = useCallback(async () => {
    setDownloadState('loading');

    try {
      // downloadCsvFiles now fetches from API internally, with fallback to local state
      const result = await downloadCsvFiles(cards, learnedWords, verbCards, learnedVerbs, reviews);

      if (result.success) {
        showToast('success', 'CSV-Dateien wurden heruntergeladen');
      } else {
        showToast('error', 'Export fehlgeschlagen. Bitte versuche es erneut.');
      }
    } catch (error) {
      showToast('error', 'Export fehlgeschlagen. Bitte versuche es erneut.');
    }

    setDownloadState('idle');
  }, [cards, learnedWords, verbCards, learnedVerbs, reviews, showToast]);

  const handleOpenEmailDialog = useCallback(() => {
    setEmailAddress('');
    setEmailError('');
    setShowEmailDialog(true);
  }, []);

  const handleCloseEmailDialog = useCallback(() => {
    setShowEmailDialog(false);
    setEmailAddress('');
    setEmailError('');
  }, []);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleEmailChange = useCallback((e) => {
    setEmailAddress(e.target.value);
    if (emailError) setEmailError('');
  }, [emailError]);

  const handleSendEmail = useCallback(async () => {
    const trimmedEmail = emailAddress.trim();

    if (!trimmedEmail) {
      setEmailError('Bitte gib eine E-Mail-Adresse ein');
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setEmailError('Bitte gib eine gültige E-Mail-Adresse ein');
      return;
    }

    setEmailState('loading');
    setEmailError('');

    const result = await sendCsvViaEmail(trimmedEmail, {
      cards,
      learnedWords,
      verbCards,
      learnedVerbs,
      reviews,
    });

    setEmailState('idle');

    if (result.success) {
      setShowEmailDialog(false);
      showToast('success', result.message || `Export wurde an ${trimmedEmail} gesendet`);
    } else {
      setEmailError(result.error || 'Export fehlgeschlagen. Bitte versuche es erneut.');
    }
  }, [emailAddress, cards, learnedWords, verbCards, learnedVerbs, reviews, showToast]);

  const handleEmailKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSendEmail();
    }
    if (e.key === 'Escape') {
      handleCloseEmailDialog();
    }
  }, [handleSendEmail, handleCloseEmailDialog]);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      handleCloseEmailDialog();
    }
  }, [handleCloseEmailDialog]);

  const totalVocab = cards.length + learnedWords.length;
  const totalVerbs = verbCards.length + learnedVerbs.length;

  return (
    <>
      <div className="data-export-section">
        <h3 className="data-export-section__title">
          <span className="data-export-section__title-icon" role="img" aria-hidden="true">💾</span>
          Daten exportieren
        </h3>
        <p className="data-export-section__description">
          Exportiere deine Vokabeln ({totalVocab}) und Verbkarten ({totalVerbs}) als CSV-Dateien
          — zum Herunterladen oder per E-Mail versenden.
        </p>

        <div className="data-export-section__buttons">
          <button
            className="data-export-btn data-export-btn--primary"
            type="button"
            onClick={handleDownload}
            disabled={downloadState === 'loading'}
            aria-label="CSV-Dateien herunterladen"
          >
            {downloadState === 'loading' ? (
              <>
                <span className="data-export-btn__loading" aria-hidden="true" />
                Wird erstellt…
              </>
            ) : (
              <>
                <span className="data-export-btn__icon" role="img" aria-hidden="true">📥</span>
                CSV herunterladen
              </>
            )}
          </button>

          <button
            className="data-export-btn"
            type="button"
            onClick={handleOpenEmailDialog}
            disabled={emailState === 'loading'}
            aria-label="CSV-Dateien per E-Mail senden"
          >
            <span className="data-export-btn__icon" role="img" aria-hidden="true">📧</span>
            Per E-Mail senden
          </button>
        </div>

        {toastMessage && (
          <div
            className={`data-export-toast data-export-toast--${toastMessage.type}`}
            role="alert"
          >
            {toastMessage.type === 'success' ? '✅' : '❌'} {toastMessage.text}
          </div>
        )}
      </div>

      {showEmailDialog && (
        <div
          className="data-export-email-overlay"
          onClick={handleOverlayClick}
          role="dialog"
          aria-modal="true"
          aria-label="Export per E-Mail senden"
        >
          <div className="data-export-email-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="data-export-email-dialog__icon" role="img" aria-hidden="true">
              📧
            </div>
            <h3 className="data-export-email-dialog__title">
              Export per E-Mail
            </h3>
            <p className="data-export-email-dialog__description">
              Gib die E-Mail-Adresse ein, an die dein Vokabelkasten-Export
              ({totalVocab} Vokabeln, {totalVerbs} Verbkarten) gesendet werden soll.
            </p>

            <div className="data-export-email-dialog__input-group">
              <label
                className="data-export-email-dialog__label"
                htmlFor="export-email-input"
              >
                E-Mail-Adresse
              </label>
              <input
                ref={emailInputRef}
                id="export-email-input"
                className={`data-export-email-dialog__input ${emailError ? 'data-export-email-dialog__input--error' : ''}`}
                type="email"
                value={emailAddress}
                onChange={handleEmailChange}
                onKeyDown={handleEmailKeyDown}
                placeholder="deine@email.de"
                autoComplete="email"
                autoCorrect="off"
                spellCheck="false"
                disabled={emailState === 'loading'}
              />
              {emailError && (
                <span className="data-export-email-dialog__error">
                  {emailError}
                </span>
              )}
            </div>

            <div className="data-export-email-dialog__actions">
              <button
                className="data-export-email-dialog__btn data-export-email-dialog__btn--cancel"
                type="button"
                onClick={handleCloseEmailDialog}
                disabled={emailState === 'loading'}
              >
                Abbrechen
              </button>
              <button
                className="data-export-email-dialog__btn data-export-email-dialog__btn--send"
                type="button"
                onClick={handleSendEmail}
                disabled={emailState === 'loading' || !emailAddress.trim()}
              >
                {emailState === 'loading' ? (
                  <>
                    <span className="data-export-btn__loading" aria-hidden="true" />
                    Senden…
                  </>
                ) : (
                  'Senden'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DataExportSection;